import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environment/environment';
import { homeRouteForRole } from '@guards/portal-role.guard';
import { TranslocoService } from '@jsverse/transloco';
import type { EmailLinkProof } from '@libs/dtos/auth/auth.dto';
import { LanguageDetectionService } from '@services/domain/language-detection.service';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { LoadingGkService } from '@services/ui/loading.service';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AnalyticsService } from '../analytics/analytics.service';
import { SessionCacheRegistry } from '../shared/session-cache.registry';
import { CurrentUserService } from './current-user.service';

/**
 * ¿El servidor ha rechazado el refresh token, o solo no pudo contestar?
 *
 * Solo el rechazo (400 `invalid_grant` o 401) significa que la sesión está
 * muerta de verdad. Un 429, un 5xx o una petición que no llega dicen que no se
 * pudo preguntar — y de eso no se deduce que el usuario tenga que volver a
 * entrar.
 */
export function isRefreshRejected(error: { status?: number } | null | undefined): boolean {
	return error?.status === 400 || error?.status === 401;
}

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	private readonly TOKEN_KEY = 'JWT_TOKEN';
	private readonly REFRESH_TOKEN_KEY = 'refresh_token';
	private readonly DEVICE_TOKEN_KEY = 'device_token';
	private readonly loginUrl = `${environment.pathBackend}/auth/login`;
	private readonly refreshUrl = `${environment.pathBackend}/auth/refresh`;
	private readonly devLoginUrl = `${environment.pathBackend}/auth/dev-login`;
	private refreshTokenTimeout: any;
	/** El reintento a 30s de la rama de error, para poder matarlo en el logout. */
	private refreshRetryTimeout: any;

	private readonly sessionCaches = inject(SessionCacheRegistry);
	private readonly analytics = inject(AnalyticsService);

	constructor(
		private http: HttpClient,
		private router: Router,
		private messageAlertService: MessageAlertService,
		private currentUserService: CurrentUserService,
		private loadingGkService: LoadingGkService,
		private ngZone: NgZone,
		private translocoService: TranslocoService,
		private languageDetectionService: LanguageDetectionService,
	) {}

	/**
	 * Gets the current access token
	 * @returns The JWT token or null if it doesn't exist
	 */
	getToken(): string | null {
		return localStorage.getItem(this.TOKEN_KEY);
	}

	/**
	 * Clears all session data (tokens, user data)
	 * Used both on logout and at the start of login to ensure clean state
	 */
	clearSession(): void {
		// Stop refresh timer
		this.stopRefreshTokenTimer();

		// Clear tokens
		localStorage.removeItem(this.TOKEN_KEY);
		localStorage.removeItem(this.REFRESH_TOKEN_KEY);

		// Clear user data
		this.currentUserService.clearCurrentUser();

		// Y las cachés de los servicios `root`, que es lo que NO se limpiaba.
		// `logout()` solo navega a `/login` —no recarga la página—, así que la
		// cuenta siguiente heredaba el `candidateMe$` de la anterior (con sus URLs
		// firmadas de documentos dentro) y sus permisos. Cada servicio con caché se
		// registra solo; ver `SessionCacheRegistry`.
		this.sessionCaches.clearAll();
	}

	/**
	 * Handles navigation after login
	 */
	private handlePostLoginNavigation(): Observable<any> {
		// Portal roles (candidate / company_*) land on their portal; staff on the backoffice.
		const home = homeRouteForRole(this.currentUserService.getUserRoleName());
		return from(this.languageDetectionService.detectAndSetLanguage()).pipe(
			tap(() => {
				this.navigateTo(home);
			}),
			catchError(() => {
				this.navigateTo(home);
				return of(null);
			}),
		);
	}

	/**
	 * Navega a una ruta usando ngZone
	 */
	private navigateTo(route: string): void {
		this.ngZone.run(() => {
			setTimeout(() => {
				this.router.navigate([route]);
			}, 150);
		});
	}

	/**
	 * Asks for a fresh activation link once the one in the email expired.
	 * The backend answers the same whether or not the address has an account.
	 */
	resendActivationEmail(email: string): Observable<any> {
		return this.http.post(`${environment.pathBackend}/auth/resend-activation`, { email }).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.status === 429
						? this.translocoService.translate('auth.errors.rateLimited')
						: error.error?.message || this.translocoService.translate('auth.errors.internalServerError');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Turns a login response into a session: tokens, refresh timer, current
	 * user, and the landing navigation.
	 *
	 * ONE copy (10/09). `login()` and `devLogin()` each carried this block, and
	 * the account activation would have been the third — three places to keep
	 * in step every time the response grows a field.
	 */
	private adoptSession(response: any): Observable<any> {
		if (!response?.access_token) {
			throw new Error('No access token received');
		}

		localStorage.setItem(this.TOKEN_KEY, response.access_token);
		localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refresh_token);
		this.startRefreshTokenTimer(response);

		this.currentUserService.setCurrentUser(AuthService.toCurrentUser(response));
		// Every session opens here (login, activation, dev-login), so this is where
		// the funnel milestones get picked up — `first_login` has just been recorded
		// by the same response. Fire-and-forget: it never delays the navigation.
		this.analytics.claimMilestones();

		return this.handlePostLoginNavigation().pipe(map(() => response));
	}

	/**
	 * La respuesta del servidor convertida en el usuario que se guarda.
	 *
	 * UNA sola copia (12/09): la llevaba `adoptSession` dentro, y ahora la
	 * necesita también el refresco de permisos del arranque, que contesta con la
	 * misma forma menos los tokens. Dos copias dejan de coincidir en cuanto la
	 * respuesta gana un campo.
	 */
	private static toCurrentUser(response: any) {
		const employee = response?.employee;
		const user = response?.user;

		if (!employee) {
			throw new Error('No employee data received');
		}

		const role = employee.role;
		if (!role) {
			throw new Error('No role data received');
		}

		return {
			id: user?.id,
			userId: employee.userId,
			email: employee.email,
			nombre: `${employee.name} ${employee.lastName}`.trim() || 'Usuario',
			tipo: 'employee',
			role: {
				id: role.id,
				name: role.name,
				description: role.description,
			},
			permissions: employee.permissions || [],
			enabled: true,
			emailVerified: user?.user_metadata?.email_verified || false,
			profilePhotoUrl: employee.profilePhotoUrl || null,
			preferredLanguage: response?.preferredLanguage || 'en',
		};
	}

	/**
	 * Vuelve a preguntar quién es el usuario y QUÉ PUEDE HACER (12/09).
	 *
	 * Lo llama el arranque de la aplicación. Hasta ahora los permisos se
	 * guardaban al iniciar sesión y nadie volvía a mirarlos, así que al recargar
	 * una URL el guard de esa pantalla decidía con una foto que podía ser de días
	 * antes: si el equipo había cambiado los permisos del rol, la persona
	 * terminaba en el inicio sin explicación (punto 9 de la clienta).
	 *
	 * **Nunca revienta ni cierra la sesión.** Si la petición no sale —sin red, un
	 * 5xx— se sigue con lo que hubiera guardado, que es el comportamiento de
	 * siempre: un servidor que no contesta no es motivo para echar a nadie. Un
	 * 401 lo resuelve el interceptor, que es quien sabe refrescar el token.
	 */
	refreshCurrentUser(): Observable<unknown> {
		// Sin sesión no hay nada que refrescar: en el login y en las pantallas
		// públicas esta llamada sería un 401 garantizado en cada arranque.
		if (!this.getToken()) {
			return of(null);
		}

		return this.http.get<any>(`${environment.pathBackend}/auth/me`).pipe(
			tap((response) => this.currentUserService.setCurrentUser(AuthService.toCurrentUser(response))),
			catchError(() => of(null)),
		);
	}

	/**
	 * Activates an invited account: sets the password the person chose and
	 * opens the session with it, straight from the invitation link.
	 *
	 * The backend answers with the login response, so the session is adopted
	 * exactly like a password login. A 400 `PASSWORD_TOO_SHORT:<n>` is the
	 * admin's minimum; a 401 is a link that expired or was already used.
	 * @param accessToken Token carried by the invitation link (`#access_token`)
	 * @param newPassword Password chosen on the activation screen
	 */
	activateAccount(link: EmailLinkProof, newPassword: string, privacyAccepted?: boolean): Observable<any> {
		this.clearSession();

		return this.http
			.post<any>(`${environment.pathBackend}/auth/activate-account`, {
				...link,
				newPassword,
				...(privacyAccepted === undefined ? {} : { privacyAccepted }),
			})
			.pipe(
				// A migrated candidate still has to tick the privacy box: no session
				// yet, and the screen retries with the `activation_token` it carries
				// (the link's token_hash is spent by then).
				switchMap((response: any) => (response?.privacy_required ? of(response) : this.adoptSession(response))),
				catchError((error: HttpErrorResponse) => {
					const backendMessage: string | undefined = error.error?.message;
					let errorMessage: string;
					if (backendMessage?.startsWith('PASSWORD_TOO_SHORT:')) {
						errorMessage = this.translocoService.translate('auth.errors.passwordTooShort', {
							min: backendMessage.split(':')[1],
						});
					} else if (error.status === 401) {
						errorMessage = this.translocoService.translate('auth.activateAccount.messages.invalidLink');
					} else {
						errorMessage =
							backendMessage || this.translocoService.translate('auth.errors.internalServerError');
					}
					this.messageAlertService.showMessage('error', 'Error', errorMessage);
					// The code rides along so the screen can react (draw the privacy box).
					return throwError(() => Object.assign(new Error(errorMessage), { code: backendMessage }));
				}),
			);
	}

	/**
	 * Logs in by sending credentials to the backend.
	 * @param email User's email
	 * @param password User's password
	 * @returns Observable with backend response
	 */
	login(email: string, password: string): Observable<any> {
		// Clear any previous session data before starting new login
		this.clearSession();

		const deviceToken = localStorage.getItem(this.DEVICE_TOKEN_KEY);
		const loginData: any = {
			email: email,
			password: password,
		};

		// Include device token if available
		if (deviceToken) {
			loginData.deviceToken = deviceToken;
		}

		return this.http.post<any>(this.loginUrl, loginData).pipe(
			switchMap((response: any) => {
				// If MFA is required, return response without further processing
				if (response?.mfa_required) {
					return of(response);
				}

				return this.adoptSession(response);
			}),
			catchError((error: HttpErrorResponse) => {
				const backendMessage = error.error?.message;
				const backendCode = error.error?.code;

				// Translation map from backend messages to translation keys
				const translationKeys: Record<string, string> = {
					'Incorrect password': 'auth.errors.incorrectPassword',
					'User not found': 'auth.errors.userNotFound',
					'Invalid credentials': 'auth.errors.invalidCredentials',
				};

				// Por código antes que por mensaje: un 429 del limitador o un
				// proveedor que no contesta NO son unas credenciales malas, y
				// decirlo así manda a cambiar una contraseña que era correcta.
				const codeKeys: Record<string, string> = {
					AUTH_RATE_LIMITED: 'auth.errors.rateLimited',
					AUTH_PROVIDER_UNAVAILABLE: 'auth.errors.providerUnavailable',
				};

				let errorMessage: string;
				if (error.status === 0) {
					// The request never reached us (network, proxy, firewall). Saying
					// "Internal server error" here sent us hunting for a 500 that
					// never happened (24/09).
					errorMessage = this.translocoService.translate('errors.networkError');
				} else if (backendCode && codeKeys[backendCode]) {
					errorMessage = this.translocoService.translate(codeKeys[backendCode]);
				} else if (backendMessage && translationKeys[backendMessage]) {
					errorMessage = this.translocoService.translate(translationKeys[backendMessage]);
				} else {
					errorMessage = backendMessage || this.translocoService.translate('auth.errors.internalServerError');
				}

				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Localhost-only development login. Signs in by email without a password
	 * (backend gates it to localhost + non-production). Mirrors the success
	 * handling of `login()`; MFA is intentionally skipped.
	 * @param email Email of the seeded user to sign in as
	 */
	devLogin(email: string): Observable<any> {
		this.clearSession();

		return this.http.post<any>(this.devLoginUrl, { email }).pipe(
			switchMap((response: any) => {
				return this.adoptSession(response);
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.internalServerError');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Verifies email using token from verification link
	 * @param token Verification token
	 * @param type Token type (optional)
	 * @returns Observable with verification response
	 */
	verifyEmail(token: string, type?: string): Observable<any> {
		this.loadingGkService.showLoading();

		return this.http.post<any>(`${environment.pathBackend}/auth/verify-email`, { token, type }).pipe(
			tap(() => {
				this.messageAlertService.showMessage(
					'success',
					this.translocoService.translate('common.labels.success'),
					this.translocoService.translate('auth.errors.emailVerified'),
				);
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorVerifyingEmail');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
			finalize(() => this.loadingGkService.hideLoading()),
		);
	}

	/**
	 * Resends verification email
	 * @param email Email to resend verification to
	 * @returns Observable with response
	 */
	resendVerificationEmail(email: string): Observable<any> {
		this.loadingGkService.showLoading();

		return this.http.post<any>(`${environment.pathBackend}/auth/resend-verification`, { email }).pipe(
			tap(() => {
				this.messageAlertService.showMessage(
					'info',
					this.translocoService.translate('auth.resetPassword.messages.emailSent'),
					this.translocoService.translate('auth.errors.verificationEmailSent'),
				);
			}),
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorResendingEmail');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
			finalize(() => this.loadingGkService.hideLoading()),
		);
	}

	/**
	 * Logs out by sending refresh token to backend and removing tokens from local storage.
	 * @returns Observable with backend response
	 */
	logout(): Observable<any> {
		// Get both tokens before clearing them
		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
		const accessToken = localStorage.getItem(this.TOKEN_KEY);

		// Clear all session data
		this.clearSession();

		// If both tokens exist, notify backend (both are required by external auth service)
		if (refreshToken && accessToken) {
			return this.http
				.post(`${environment.pathBackend}/auth/logout`, {
					refresh_token: refreshToken,
					access_token: accessToken,
				})
				.pipe(
					tap(() => {
						this.router.navigate(['/login']);
					}),
					catchError(() => {
						// Even if it fails, navigate to login
						this.router.navigate(['/login']);
						return of(null);
					}),
				);
		}

		// If no tokens available, just navigate to login
		this.router.navigate(['/login']);
		return of(null);
	}

	/**
	 * Checks if the user is authenticated.
	 * @returns `true` if token exists and has not expired.
	 */
	isAuthenticated(): boolean {
		const token = localStorage.getItem(this.TOKEN_KEY);
		if (!token) {
			return false;
		}

		try {
			const payload = JSON.parse(atob(token.split('.')[1]));
			const isExpired = payload.exp * 1000 < Date.now();

			if (isExpired) {
				return false;
			}

			return true;
		} catch {
			return false;
		}
	}

	/**
	 * Refreshes the access token using the refresh token
	 * @returns Observable with the new token
	 */
	private retryCount = 0;
	private maxRetries = 3;

	refreshToken(): Observable<any> {
		const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);

		if (!refreshToken) {
			return throwError(() => new Error('No refresh token available'));
		}

		return this.http.post<any>(this.refreshUrl, { refresh_token: refreshToken }).pipe(
			tap((response) => {
				if (!response?.access_token || !response?.refresh_token) {
					throw new Error('Invalid refresh response');
				}

				this.retryCount = 0; // Reset retry count on success
				localStorage.setItem(this.TOKEN_KEY, response.access_token);
				localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refresh_token);
				this.startRefreshTokenTimer(response);
			}),
			catchError((error: HttpErrorResponse) => {
				// Cerrar sesión SOLO si el servidor ha rechazado el refresh token
				// (400/401: caducado, revocado o ya rotado). Antes bastaba
				// cualquier 4xx y, agotados los reintentos, también cualquier otro
				// fallo — así que un **429** del limitador, un 502 de un momento
				// malo o un corte de red de dos segundos mataban una sesión que
				// seguía siendo válida. Es el "error raro y te desloguea": el
				// usuario no había hecho nada y su token no tenía ningún problema.
				//
				// Lo demás se reintenta con espera creciente y, si se agotan los
				// intentos, se propaga el error y la sesión SE QUEDA. La pantalla
				// enseñará su aviso y el siguiente intento refrescará bien.
				const shouldRetry = !isRefreshRejected(error) && this.retryCount < this.maxRetries;

				if (shouldRetry) {
					this.retryCount++;
					const backoffTime = 2 ** this.retryCount * 1000;
					return new Observable((subscriber) => {
						setTimeout(() => {
							this.refreshToken().subscribe(subscriber);
						}, backoffTime);
					});
				}

				this.retryCount = 0;
				if (isRefreshRejected(error)) {
					// logout() returns a cold Observable — subscribe so the HTTP fires
					// and the navigation to /login actually happens.
					this.logout().subscribe();
				}
				return throwError(() => error);
			}),
		);
	}

	/**
	 * Starts the timer to refresh the token
	 * @param tokens Access and refresh tokens
	 */
	private startRefreshTokenTimer(tokens: any) {
		// `expires_in` (segundos que le quedan al token, según el servidor) y NO la
		// hora de caducidad comparada con el reloj del equipo. Con un reloj
		// desajustado —que los hay— la resta daba un token "recién caducado" nada
		// más recibirlo: refresco inmediato, respuesta que vuelve a parecer
		// caducada y otra vez, en bucle. `exp` solo se usa si el servidor no manda
		// `expires_in`.
		const expiresInSeconds = Number(tokens?.expires_in);
		let timeUntilExpiry: number;
		if (Number.isFinite(expiresInSeconds) && expiresInSeconds > 0) {
			timeUntilExpiry = expiresInSeconds * 1000;
		} else {
			const jwtToken = JSON.parse(atob(tokens.access_token.split('.')[1]));
			timeUntilExpiry = new Date(jwtToken.exp * 1000).getTime() - Date.now();
		}

		// If token expires in less than 2 minutes, refresh immediately
		if (timeUntilExpiry < 120000) {
			// Suelo de 30 s: si aun así saliera un valor raro, se reintenta con
			// calma en vez de encadenar refrescos (que acaban en 429 del limitador).
			this.refreshTokenTimeout = setTimeout(() => this.refreshToken().subscribe({ error: () => {} }), 30_000);
			return;
		}

		// Refresh when 1/3 of token lifetime remains
		const refreshDelay = Math.floor(timeUntilExpiry * 0.66);

		// Clear any existing timeout
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}

		// Set new timeout
		this.refreshTokenTimeout = setTimeout(() => {
			this.refreshToken().subscribe({
				next: () => {},
				error: () => {
					// El handle SE GUARDA: sin ello, un refresh fallido seguido de
					// logout lanzaba un POST /auth/refresh 30 segundos después, con
					// la sesión ya borrada y sin nadie escuchando el error.
					this.refreshRetryTimeout = setTimeout(
						() => this.refreshToken().subscribe({ error: () => {} }),
						30000,
					);
				},
			});
		}, refreshDelay);
	}

	/**
	 * Stops the token refresh timer
	 */
	private stopRefreshTokenTimer() {
		if (this.refreshTokenTimeout) {
			clearTimeout(this.refreshTokenTimeout);
		}
		if (this.refreshRetryTimeout) {
			clearTimeout(this.refreshRetryTimeout);
			this.refreshRetryTimeout = undefined;
		}
	}

	/**
	 * Sends a password reset email to the user
	 * @param email User's email address
	 * @returns Observable with the reset response
	 */
	resetPassword(email: string): Observable<any> {
		return this.http.post(`${environment.pathBackend}/auth/reset-password`, { email }).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorSendingResetEmail');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Updates user password using the access token from reset email
	 * @param accessToken Token from reset email
	 * @param newPassword New password
	 * @returns Observable with the update response
	 */
	updatePasswordWithToken(link: EmailLinkProof, newPassword: string): Observable<any> {
		return this.http
			.post(`${environment.pathBackend}/auth/update-password`, {
				...link,
				newPassword,
			})
			.pipe(
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.errorUpdatingPassword');
					this.messageAlertService.showMessage('error', 'Error', errorMessage);
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Enrolls the current user in MFA/2FA
	 * @returns Observable with enrollment data including QR code
	 */
	enrollMFA(): Observable<any> {
		return this.http.post(`${environment.pathBackend}/auth/mfa/enroll`, {}).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorEnrollingMFA');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Verifies MFA enrollment with TOTP code
	 * @param factorId Factor ID from enrollment
	 * @param code TOTP code from authenticator app
	 * @returns Observable with verification response
	 */
	verifyMFAEnrollment(factorId: string, code: string): Observable<any> {
		return this.http.post(`${environment.pathBackend}/auth/mfa/verify`, { factorId, code }).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.invalidVerificationCode');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Unenrolls the current user from MFA/2FA
	 * @param factorId Factor ID to remove
	 * @returns Observable with unenrollment response
	 */
	unenrollMFA(factorId: string): Observable<any> {
		return this.http.post(`${environment.pathBackend}/auth/mfa/unenroll`, { factorId }).pipe(
			catchError((error: HttpErrorResponse) => {
				const errorMessage =
					error.error?.message || this.translocoService.translate('auth.errors.errorDisablingMFA');
				this.messageAlertService.showMessage('error', 'Error', errorMessage);
				return throwError(() => new Error(errorMessage));
			}),
		);
	}

	/**
	 * Gets list of MFA factors for the current user
	 * @returns Observable with list of MFA factors
	 */
	listMFAFactors(): Observable<any> {
		return this.http.get(`${environment.pathBackend}/auth/mfa/factors`).pipe(
			catchError(() => {
				return of({ factors: [] });
			}),
		);
	}

	/**
	 * Verifies MFA code during login
	 * @param accessToken Temporary access token from login
	 * @param factorId MFA factor ID
	 * @param code TOTP code from authenticator app
	 * @param trustDevice Whether to trust this device for 30 days
	 * @returns Observable with complete login response
	 */
	verifyMFALogin(accessToken: string, factorId: string, code: string, trustDevice: boolean = false): Observable<any> {
		// Clear any previous session data before completing MFA login
		this.clearSession();

		return this.http
			.post(`${environment.pathBackend}/auth/mfa/verify-login`, {
				accessToken,
				factorId,
				code,
				trustDevice,
			})
			.pipe(
				tap((response: any) => {
					if (!response?.access_token) {
						throw new Error('No access token received after MFA verification');
					}

					// Save device token if provided
					if (response.device_token) {
						localStorage.setItem(this.DEVICE_TOKEN_KEY, response.device_token);
					}

					// Save tokens
					const token = response.access_token;
					const refreshToken = response.refresh_token;
					localStorage.setItem(this.TOKEN_KEY, token);
					localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);

					// Start refresh timer
					this.startRefreshTokenTimer(response);

					// Get employee data from response
					const employee = response.employee;
					const user = response.user;

					if (!employee) {
						throw new Error('No employee data received');
					}

					const role = employee.role;
					if (!role) {
						throw new Error('No role data received');
					}

					const completeUserData = {
						id: user.id,
						userId: employee.userId,
						email: employee.email,
						nombre: `${employee.name} ${employee.lastName}`.trim() || 'Usuario',
						tipo: 'employee',
						role: {
							id: role.id,
							name: role.name,
							description: role.description,
						},
						permissions: employee.permissions || [],
						enabled: true,
						emailVerified: user.user_metadata?.email_verified || false,
						profilePhotoUrl: employee.profilePhotoUrl || null,
						preferredLanguage: response.preferredLanguage || 'en',
					};

					// Save complete data in CurrentUserService
					this.currentUserService.setCurrentUser(completeUserData);
				}),
				// After saving user data, handle navigation (which also loads preferred language)
				switchMap((response) => this.handlePostLoginNavigation().pipe(map(() => response))),
				catchError((error: HttpErrorResponse) => {
					const errorMessage =
						error.error?.message || this.translocoService.translate('auth.errors.invalidMFACode');
					return throwError(() => new Error(errorMessage));
				}),
			);
	}

	/**
	 * Gets list of trusted devices for the current user
	 * @returns Observable with list of trusted devices
	 */
	getTrustedDevices(): Observable<any> {
		return this.http.get(`${environment.pathBackend}/auth/trusted-devices`).pipe(
			catchError(() => {
				return of({ devices: [] });
			}),
		);
	}

	/**
	 * Revokes a trusted device
	 * @param deviceId ID of the device to revoke
	 * @returns Observable with the result
	 */
	revokeTrustedDevice(deviceId: number): Observable<any> {
		return this.http.delete(`${environment.pathBackend}/auth/trusted-devices/${deviceId}`).pipe(
			catchError((error: HttpErrorResponse) => {
				return throwError(() => error);
			}),
		);
	}
}
